// Sugar Alarm — cycle 452: the alarm controls, off `/now`.
//
// A compact bar that carries the SAME low-side alarm actions the matching
// `/now` surface carries, through the SAME episode handlers, wherever the
// caregiver is standing when those surfaces are not on screen. See
// `lib/alarmControl.ts` for when it shows and what it offers; this file only
// draws it.
//
// ⚠ IT ADDS NO ALARM BEHAVIOUR. Every button calls a handler `NowScreen`'s
// surfaces already call — `episode.address()` (Snooze), `episode.stop`
// (Stop, low only), `episode.soundNow()` (the lead screen), and
// `episode.retryAck` (the cycle-416 failure notice). Nothing here writes
// alarm, custody, snooze or recovery state of its own, and there is no
// endpoint it can reach that `/now` cannot.
//
// ⚠ URGENT LOW IS SNOOZE-ONLY HERE TOO (cycle 73). `stop` is only ever
// offered for `low`, and `episode.stop` refuses urgent low regardless.
//
// ⚠ ACTIONS WHOSE HANDLERS LIVE IN `NowScreen` BECOME "GO TO ALARM".
// "I'm treating this" / "I've got <name>" run `NowScreen`'s `beginTreating`,
// with its busy state, its refusal line and the identity sheet; the custody
// question needs its question text and countdown. None of that is lifted
// here — a second copy of the custody door is how the two drift. The
// caller decides what "go" means (the shell navigates to `/now` with
// replace; the Log overlay closes itself), and on the pairing family the
// button is simply absent: a handshake broken mid-flow can strand a device
// unpaired.
//
// ⚠ ANNOUNCED ONCE. The bar re-renders on every reading; an `aria-live`
// wrapper would read the number out on every tick. A single hidden status
// line is filled in after mount (and again only if the LEVEL changes, which
// is a new alarm), so assistive tech hears it appear, once.
//
// ⚠ CYCLE 452.2 — IT IS NOW's BANNER, OFF NOW. Operator decision 2026-09-25,
// from a photo of the A9+ on `/pair-device` during an urgent low: the 452
// control floated there as a small pastel card (white on light coral, about
// 2.5:1) and nothing on screen read as urgent. It is now a full-width banner
// wherever Now's banner is on that surface — along the BOTTOM of the kiosk
// stage (cycle 394), UNDER THE HEADER on phones and web — built from the
// SAME styles Now's Low / High / Urgent banners use (`alarmBannerStyle.ts`),
// so the two cannot drift. The frame decides the position (the kiosk slot is
// `.kioskAlarmBanner` in tokens.css); this file draws the banner. Only the
// look moved: when it shows, what it offers and every handler are 452's.
//
// ⚠ CYCLE 454 — HIGH AND URGENT HIGH GET THIS BANNER TOO (operator decision).
// Same component, slot and page rules; the level ("HIGH" / "URGENT HIGH"),
// the reading, and a right-aligned Stop pill (`AlarmStopPill`: compact pill,
// 44 × 44 px target) — never Snooze. The high row does not take the
// portrait-phone stacking classes: a lone right-aligned pill on the label's
// line is the operator's layout, not a full-width second line.
//
// ⚠ CYCLE 460 — A PLAIN LOW DRAWS THE LOW BANNER'S PILLS (operator decision).
// Snooze `filled` + Stop `outlined`, labelled "Snooze sounds" / "Stop
// sounds", the same shared `AlarmPill` Now's `LowAlarmBanner` draws, right-
// aligned at one width. Cycle 461: on the label's row everywhere, reading
// "Snooze" / "Stop" on a portrait phone (CSS), so it is the high banner's
// height (operator decision).
// Urgent low keeps its buttons exactly as before (Snooze-only, cycle 73).

import { useEffect, useState } from "react";

import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import { compactAlarmActions } from "../../lib/alarmControl";
import type { DisplayGlucose } from "../../lib/currentGlucose";
import { displayValue, trendInfo } from "../../lib/glucose";
import AckFailureNotice from "./AckFailureNotice";
import {
  AlarmPill,
  AlarmPillPair,
  AlarmStopPill,
  LOW_SNOOZE_LABEL,
  LOW_SNOOZE_SHORT_LABEL,
  LOW_STOP_LABEL,
  LOW_STOP_SHORT_LABEL,
} from "./AlarmStopPill";
import styles from "./CompactAlarmControl.module.css";
// The portrait-phone stacking Now's banners use too (one rule, no drift).
import stack from "./AlarmBanner.module.css";
import {
  alarmBannerDot,
  alarmBannerGround,
  alarmPrimaryButton,
  alarmSecondaryButton,
  alarmTone,
} from "./alarmBannerStyle";

export type CompactAlarmPlacement =
  /** The frame's alarm-banner slot: full width along the bottom of the
   *  kiosk stage (the Android pairing takeover included), under the header
   *  on phones and web — where Now's own banner sits on that surface. */
  | "banner"
  /** Docked at the top of the Log overlay on `/now` (kept at the top by
   *  operator decision 2026-09-25: a bottom dock sits under the tablet's
   *  number keyboard). */
  | "docked";

export interface CompactAlarmControlProps {
  episode: AlarmEpisodeState;
  /** The selector-released current value (`selectCurrentGlucoseFor`), the
   *  same one Now's hero paints. Null = withheld. */
  current: DisplayGlucose | null;
  units: "mg/dL" | "mmol/L";
  placement: CompactAlarmPlacement;
  /** False on the pairing family: in-place actions only, never "Go to
   *  alarm". */
  canNavigate: boolean;
  onGoToAlarm: () => void;
}

export function CompactAlarmControl({
  episode,
  current,
  units,
  placement,
  canNavigate,
  onGoToAlarm,
}: CompactAlarmControlProps) {
  const actions = compactAlarmActions(episode, { canNavigate });
  const level = actions?.level ?? null;
  const isHigh = level === "high" || level === "urgent_high";
  const levelLabel =
    level === "urgent_low"
      ? "Urgent Low"
      : level === "urgent_high"
        ? "Urgent High"
        : level === "high"
          ? "High"
          : "Low";

  // Filled after mount so the insertion is what gets announced; keyed on the
  // level only, never on the reading.
  const [announcement, setAnnouncement] = useState("");
  useEffect(() => {
    setAnnouncement(level == null ? "" : `${levelLabel} alarm. Alarm controls are here.`);
  }, [level, levelLabel]);

  if (actions == null) return null;

  const trend = current ? trendInfo(current.trend, current.trendRate) : null;
  const valueText = current ? displayValue(current.sgv, units) : "No data";
  // Amber for a low, the darker urgent red for an urgent low — Now's pairs.
  const tone = alarmTone(level);
  const primary = alarmPrimaryButton(tone);
  const secondary = alarmSecondaryButton();
  const btnClass = `${styles.btn} ${stack.stackBtn}`;
  // Cycle 461: a plain low lays out like a high — one row, the pills on the
  // label's line — so the two banners are the same height (operator
  // decision). Except with a FAILED ACK: "· NOT SNOOZED" (≈ 150 px, alarm
  // state, never cut) is wider than a narrow phone leaves the label beside
  // two pills, so that row may wrap and the pills drop to their own line (a
  // failed ack may grow the banner — measured, type-floor 375 / 360). Urgent
  // low keeps the cycle-452.2 portrait stacking.
  const oneRow = isHigh || (level === "low" && episode.ackFailure == null);
  const rowClass = oneRow ? `${styles.row} ${styles.oneRow}` : `${styles.row} ${stack.stackRow}`;
  // Cycle 416 / 452.1: a failed ack names itself in the banner's own words,
  // exactly as Now's Low and High banners do, beside the notice's Retry.
  const ackSuffix =
    episode.ackFailure != null
      ? episode.ackFailure.action === "snooze"
        ? "· NOT SNOOZED"
        : "· NOT STOPPED"
      : null;

  return (
    <div
      className={`${styles.root} ${styles[placement]}`}
      style={alarmBannerGround(tone)}
      role="region"
      aria-label="Alarm controls"
      data-testid="compact-alarm-control"
      data-alarm-control="banner"
      data-placement={placement}
      data-level={level ?? undefined}
    >
      <span className={styles.srOnly} role="status" aria-live="polite">
        {announcement}
      </span>
      <div className={rowClass}>
        <span className={styles.label}>
          <span aria-hidden style={alarmBannerDot(true)} />
          {/* Each fragment is its own nowrap span, so a wrap can only fall
              BETWEEN them — "· NOT SNOOZED" is alarm state and is never cut
              off (452.1's rule). */}
          <span className={styles.level}>{levelLabel}</span>
          {ackSuffix ? (
            <span className={styles.level} data-testid="ack-suffix">
              {ackSuffix}
            </span>
          ) : null}
          <span className={styles.reading}>
            <span className={styles.sep} aria-hidden>
              ·
            </span>
            <span className={`${styles.value} tnum`} data-testid="compact-alarm-value">
              {valueText}
            </span>
            {/* The reading as Now's banner writes it ("46 mg/dL"), then the
                trend Now's hero shows beside the number. */}
            {current && <span className={styles.units}>{units}</span>}
            {current && trend && (
              <span
                className={styles.trend}
                aria-label={trend.label}
                data-testid="compact-alarm-trend"
              >
                {trend.glyph}
              </span>
            )}
          </span>
        </span>
        {isHigh ? (
          // Cycle 454: Stop only, as the right-aligned pill.
          <AlarmStopPill
            tone={tone}
            onStop={episode.stop}
            testId="compact-alarm-stop"
          />
        ) : level === "low" ? (
          // Cycle 460: a plain low draws Now's Low-banner pills — Snooze
          // `filled`, Stop `outlined`, "… sounds" — so the two banners stay in
          // parity. Which controls exist is still `compactAlarmActions`'.
          <span className={styles.actions}>
            <AlarmPillPair>
              {actions.snooze && (
                <AlarmPill
                  tone={tone}
                  label={LOW_SNOOZE_LABEL}
                  shortLabel={LOW_SNOOZE_SHORT_LABEL}
                  testId="compact-alarm-snooze"
                  marker={FILLED_MARKER}
                  onPress={() => void episode.address()}
                />
              )}
              {actions.stop && (
                <AlarmPill
                  tone={tone}
                  label={LOW_STOP_LABEL}
                  shortLabel={LOW_STOP_SHORT_LABEL}
                  appearance="outlined"
                  testId="compact-alarm-stop"
                  marker={OUTLINED_MARKER}
                  onPress={episode.stop}
                />
              )}
            </AlarmPillPair>
          </span>
        ) : (
        <span className={`${styles.actions} ${stack.stackActions}`}>
          {actions.snooze && (
            <button
              type="button"
              className={btnClass}
              style={primary}
              data-testid="compact-alarm-snooze"
              onClick={() => void episode.address()}
            >
              Snooze
            </button>
          )}
          {actions.stop && (
            <button
              type="button"
              className={btnClass}
              style={secondary}
              data-testid="compact-alarm-stop"
              onClick={episode.stop}
            >
              Stop
            </button>
          )}
          {actions.soundNow && (
            <button
              type="button"
              className={btnClass}
              style={secondary}
              data-testid="compact-alarm-sound-now"
              onClick={() => void episode.soundNow()}
            >
              Sound the alarm now
            </button>
          )}
          {actions.goToAlarm && (
            <button
              type="button"
              className={btnClass}
              style={secondary}
              data-testid="compact-alarm-go"
              onClick={onGoToAlarm}
            >
              Go to alarm
            </button>
          )}
        </span>
        )}
      </div>
      {actions.retry && (
        <div className={styles.notice}>
          {/* Cycle 416's notice, not a toast: a caregiver who missed a toast
              would go on believing the alarm was handled. It carries its
              own Retry, which re-runs the ack actually pressed. */}
          <AckFailureNotice
            failure={episode.ackFailure}
            onRetry={episode.retryAck}
            compact
          />
        </div>
      )}
    </div>
  );
}

// Cycle 460: the Low pills' inert markers (as on Now's Low banner).
const FILLED_MARKER = { "data-alarm-pill": "filled" } as const;
const OUTLINED_MARKER = { "data-alarm-pill": "outlined" } as const;
